<template>
  <div
    id="rncpSourceModal"
    ref="rncpSourceModal"
    aria-hidden="true"
    aria-labelledby="rncpSourceModal"
    aria-modal="true"
    class="modal fade"
    role="dialog"
    tabindex="-1"
  >
    <div class="modal-dialog modal-dialog-centered modal-lg" role="document">
      <div class="modal-content" v-if="object">
        <div class="modal-header">
          <h5 class="modal-title textStyle">
            {{ object.name }}
          </h5>
          <button
            aria-label="Close"
            class="btn-close"
            data-bs-dismiss="modal"
            data-dismiss="modal"
            type="button"
          ></button>
        </div>
        <div class="modal-body">
          <div class="source">
            <template v-if="object.criticality">
              <p class="source__label">{{ $t("training.rncp.source_detail.criticality.title") }}</p>
              <div :class="'source__criticality badge bg-light-' + criticalityColor">
                <span class="source__criticality__dot"></span>
                <span>{{ criticalityLabel }}</span>
              </div>
            </template>

            <template v-if="object.pertinence && typeof object.pertinence === 'number'">
              <p class="source__label">{{ $t("training.rncp.source_detail.pertinence") }}</p>
              <div class="source__pertinence">
                <span :class="'source__pertinence__value text-' + pertinenceColor">{{ object.pertinence }}%</span>
                <div class="source__pertinence__info">
                  <FontAwesomeIcon icon="fa-solid fa-circle-info" />
                  <div class="source__pertinence__tooltip">
                    <p class="source__label">{{ $t("training.rncp.source_detail.criteria.title") }}</p>
                    <ul>
                      <li>
                        {{ $t("training.rncp.source_detail.criteria.pertinence_f") }} : {{ object.pertinenceScoreF }}
                      </li>
                      <li>
                        {{ $t("training.rncp.source_detail.criteria.pertinence_a") }} : {{ object.pertinenceScoreA }}
                      </li>
                      <li>
                        {{ $t("training.rncp.source_detail.criteria.pertinence_p") }} : {{ object.pertinenceScoreP }}
                      </li>
                      <li>
                        {{ $t("training.rncp.source_detail.criteria.pertinence_d") }} : {{ object.pertinenceScoreD }}
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </template>

            <template v-if="object.justification">
              <p class="source__label">{{ $t("training.rncp.source_detail.justification") }}</p>
              <div class="source__justification">
                {{ object.justification }}
              </div>
            </template>

            <p class="source__label">{{ $t("training.rncp.source_detail.referential_presence") }}</p>
            <div class="source__references">
              <div class="source__references__item">
                <span class="source__references__item__label">{{ $t("training.rncp.source_detail.source.rome") }}</span>
                <span
                  class="source__references__item__status"
                  :class="{ 'text-success': rome, 'text-danger': !rome }"
                  >{{
                    rome ? $t("training.rncp.source_detail.present") : $t("training.rncp.source_detail.absent")
                  }}</span
                >
              </div>

              <div class="source__references__item">
                <span class="source__references__item__label">{{ $t("training.rncp.source_detail.source.esco") }}</span>
                <span
                  class="source__references__item__status"
                  :class="{ 'text-success': esco, 'text-danger': !esco }"
                  >{{
                    esco ? $t("training.rncp.source_detail.present") : $t("training.rncp.source_detail.absent")
                  }}</span
                >
              </div>

              <div class="source__references__item" v-if="isMission">
                <span class="source__references__item__label">{{ $t("training.rncp.source_detail.source.rncp") }}</span>
                <span
                  class="source__references__item__status"
                  :class="{ 'text-success': rncp, 'text-danger': !rncp }"
                  >{{
                    rncp ? $t("training.rncp.source_detail.present") : $t("training.rncp.source_detail.absent")
                  }}</span
                >
              </div>
            </div>

            <template v-if="object.offerSources && Object.keys(object.offerSources).length">
              <p class="source__label">{{ $t("training.rncp.source_detail.source_offers") }}</p>
              <ul class="source__offers">
                <li class="source__offer" v-for="offer in object.offerSources" :key="offer.id">
                  <span class="source__offer__title" v-if="offer.title || offer.company"
                    >{{ offer.title }} {{ offer.company ? " - " + offer.company : "" }}</span
                  >
                  <span class="source__offer__time" v-if="offer.publicationDate"
                    >{{ $t("training.rncp.source_detail.published_at") }} {{ formatDate(offer.publicationDate) }}</span
                  >
                  <a v-if="offer.url" target="_blank" class="source__offer__link" :href="offer.url">
                    {{ $t("training.rncp.source_detail.view_offer") }}
                    <FontAwesomeIcon icon="fa-solid fa-arrow-up-right" />
                  </a>
                </li>
              </ul>
            </template>

            <span class="source__baseline" v-if="object.jobOfferTotal && object.jobOfferCount">{{
              $t("training.rncp.source_detail.offers_count", {
                jobOfferCount: object.jobOfferCount,
                jobOfferTotal: object.jobOfferTotal,
                type: $t("studea_manager." + object.type + ".title").toLowerCase(),
              })
            }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Modal } from "bootstrap";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { trainingSkillConstante } from "@/constantes/trainingSkill";
import { i18n } from "@/i18n";
import { dateFormatter } from "@/services/utilities/date-formatter";

export default {
  name: "TrainingRncpSourceModalComponent",
  components: { FontAwesomeIcon },
  emits: ["hidden"],
  props: {
    object: {
      type: Object,
      required: true,
    },
  },
  computed: {
    rome() {
      return this.object.sourceDetails && !!this.object.sourceDetails.find((source) => source.label === "ROME");
    },
    esco() {
      return this.object.sourceDetails && !!this.object.sourceDetails.find((source) => source.label === "ESCO");
    },
    rncp() {
      return this.object.sourceDetails && !!this.object.sourceDetails.find((source) => source.label === "RNCP");
    },
    isMission() {
      return this.object.type === trainingSkillConstante.OBJECT_TYPE_MISSION;
    },
    criticalityLabel() {
      switch (parseInt(this.object.criticality)) {
        case trainingSkillConstante.CRITICALITY_LEVEL_LOW:
          return i18n.global.t("training.rncp.source_detail.criticality.low");
        case trainingSkillConstante.CRITICALITY_LEVEL_MEDIUM:
          return i18n.global.t("training.rncp.source_detail.criticality.medium");
        case trainingSkillConstante.CRITICALITY_LEVEL_HIGH:
          return i18n.global.t("training.rncp.source_detail.criticality.high");
      }
      return "";
    },
    criticalityColor() {
      switch (parseInt(this.object.criticality)) {
        case trainingSkillConstante.CRITICALITY_LEVEL_LOW:
          return "danger";
        case trainingSkillConstante.CRITICALITY_LEVEL_MEDIUM:
          return "warning";
        case trainingSkillConstante.CRITICALITY_LEVEL_HIGH:
          return "success";
      }
      return "";
    },
    pertinenceColor() {
      if (this.object.pertinence >= 85) {
        return "success";
      } else if (this.object.pertinence >= 70) {
        return "warning";
      } else {
        return "danger";
      }
    },
  },
  watch: {
    object() {
      if (this.object !== null) {
        this.openModal();
      }
    },
  },
  mounted() {
    this.init();
  },
  methods: {
    init() {
      if (this.$refs.rncpSourceModal) {
        this.$refs.rncpSourceModal.addEventListener("hidden.bs.modal", this.hidden);
      }
    },

    openModal() {
      if (this.$refs.rncpSourceModal) {
        const modal = new Modal(this.$refs.rncpSourceModal);
        modal.show();
      }
    },

    hide() {
      if (this.$refs.rncpSourceModal) {
        const modal = Modal.getOrCreateInstance(this.$refs.rncpSourceModal);
        modal.hide();
        this.hidden();
      }
    },

    formatDate(date) {
      return dateFormatter.format(date, "short");
    },

    hidden() {
      this.$emit("hidden");
    },
  },
};
</script>

<style lang="scss" scoped>
h5.modal-title {
  font-size: 18px !important;
}

.source {
  &__label {
    font-weight: 600;
    font-size: 13px !important;
    text-transform: uppercase;
    color: var(--primary-color) !important;
    margin-bottom: 5px !important;
  }

  &__criticality {
    padding: 5px 10px;
    &__dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      display: inline-block;
      margin-right: 5px;
    }

    &.bg-light-danger {
      .source__criticality__dot {
        background: $red;
      }
    }

    &.bg-light-success {
      .source__criticality__dot {
        background: $green;
      }
    }

    &.bg-light-warning {
      .source__criticality__dot {
        background: $orange;
      }
    }
  }

  &__pertinence {
    display: inline-flex;

    &__value {
      color: $black;
      font-size: 16px;
      font-weight: 600;
    }

    &__info {
      font-size: 16px;
      color: $m_color_8;
      margin-left: 5px;
      position: relative;

      &:hover .source__pertinence__tooltip {
        display: block;
      }
    }

    &__tooltip {
      display: none;
      background: $white;
      font-size: 12px;
      border: 1px solid $m_color_4;
      box-shadow:
        rgba(50, 50, 93, 0.25) 0px 6px 12px -2px,
        rgba(0, 0, 0, 0.3) 0px 3px 7px -3px;
      position: absolute;
      left: calc(100% + 20px);
      top: 50%;
      transform: translateY(-50%);
      width: 320px;
      padding: 20px;
      border-radius: $radius;

      ul {
        padding-left: 12px;
        margin-bottom: 0;
      }
    }
  }

  &__justification {
    background: $ia-color-bg;
    border-left: 4px solid $ia-color;
    padding: 10px 15px;
  }

  &__references {
    display: flex;
    align-items: center;
    &__item {
      background: $m_color_1;
      border-radius: $radius;
      border: 1px solid $m_color_4;
      width: 100px;
      padding: 5px 10px;
      display: flex;
      flex-direction: column;
      &__label {
        text-transform: uppercase;
        font-weight: 600;
      }
    }

    &__item + &__item {
      margin-left: 10px;
    }
  }

  &__offers {
    padding: 0;
    margin: 0;
  }

  &__offer {
    display: flex;
    flex-direction: column;
    padding: 10px 0 10px 16px;
    position: relative;

    &:before {
      position: absolute;
      left: 0;
      top: 17px;
      content: "";
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: $m_color_6;
    }
    &__title {
      font-weight: 600;
    }

    &__time {
      color: $m_color_8;
      font-size: 12px;
      font-style: italic;
    }

    &__link {
      color: $primary;
    }
  }

  &__offer + &__offer {
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px dashed $m_color_6;
  }

  &__baseline {
    margin-top: 15px;
    color: $m_color_8 !important;
    font-size: 12px;
  }
}

div + .source__label {
  margin-top: 25px !important;
}
</style>
